import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Save,
  ArrowLeft,
  IndianRupee,
  Table,
  Percent,
  AlertTriangle,
  Package,
  X,
  Calendar,
  Clock,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useUser } from '@/contexts/UserContext';
import axiosInstance from '@/utils/axiosInstance';
import { API_URL, USER_ROLES } from '@/constant/constant';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// --- EditableCell component ---
const EditableCell = ({
  value,
  onUpdate,
  type = 'text',
  disabled = false,
  error = false,
  placeholder = '',
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    if (type === 'number') {
      const numberRegex = /^(\d*\.?\d*)$/;
      if (inputValue === '' || numberRegex.test(inputValue)) {
        onUpdate(inputValue);
      }
      return;
    }
    onUpdate(inputValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleKeyDown = (e) => {
    if (type === 'number') {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ];
      const isDigit = e.key >= '0' && e.key <= '9';
      const isDecimal = e.key === '.' && !value?.toString().includes('.');
      const isCtrlKey = e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase());

      if (!allowedKeys.includes(e.key) && !isDigit && !isDecimal && !isCtrlKey) {
        e.preventDefault();
      }
    }
  };

  const displayValue = value?.toString() || '';

  return (
    <div className="relative w-full">
      <Input
        type={type}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        className={`h-9 text-sm focus-visible:ring-1 focus-visible:ring-offset-0 font-medium transition-all duration-200 ${type === 'number' ? 'pl-2' : 'pl-2'} pr-1 ${
          error
            ? 'border-red-400 focus:border-red-500 bg-red-50 focus-visible:ring-red-500'
            : isFocused
              ? 'border-[#F77C06] shadow-md shadow-orange-100 focus-visible:ring-[#F77C06]'
              : 'border-slate-300 hover:border-slate-400'
        } ${disabled ? 'opacity-60 bg-slate-100 cursor-not-allowed' : 'bg-white'}`}
      />
    </div>
  );
};

// --- VariantTableRow component ---
const VariantTableRow = ({ variant, onUpdate, isChanged, disabled = false }) => {
  const handleUpdate = (field, value) => {
    onUpdate(variant.id, { [field]: value });
  };

  const mrp = Number.parseFloat(variant.mrp);
  const netPrice = Number.parseFloat(variant.netPrice);

  const mrpError =
    variant.available &&
    (variant.mrp === '' ||
      variant.mrp === null ||
      variant.mrp === undefined ||
      isNaN(mrp) ||
      mrp < 0);

  const netPriceError =
    variant.available &&
    (variant.netPrice === '' ||
      variant.netPrice === null ||
      variant.netPrice === undefined ||
      isNaN(netPrice) ||
      netPrice < 0 ||
      netPrice > mrp);

  return (
    <div
      className={`grid sm:grid-cols-[1fr,86px,86px,66px,48px] grid-cols-[60px,64px,64px,64px,48px] gap-x-2 px-3 py-3 items-center transition-all duration-300 border-b border-slate-200 ${
        isChanged ? 'bg-orange-50 border-l-4 border-l-[#F77C06]' : 'bg-white hover:bg-slate-50'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <div className="flex flex-col pr-2">
        <span className="text-sm font-bold text-slate-900 line-clamp-2">
          {variant.variant} {variant.unit}
        </span>
        {(variant.soldQuantity || 0) > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[10px] text-slate-400 font-bold uppercase">Sold:</span>
            <span className="text-[10px] text-orange-600 font-bold">{variant.soldQuantity}</span>
          </div>
        )}
      </div>

      <div className="flex items-center">
        <EditableCell
          value={variant.mrp}
          onUpdate={(value) => handleUpdate('mrp', value)}
          type="number"
          disabled={disabled}
          placeholder="0"
          error={mrpError}
        />
      </div>

      <div className="flex items-center">
        <EditableCell
          value={variant.netPrice}
          onUpdate={(value) => handleUpdate('netPrice', value)}
          type="number"
          disabled={disabled}
          placeholder="0"
          error={netPriceError}
        />
      </div>

      <div className="flex items-center">
        <Input
          type="number"
          value={variant.totalQuantity?.toString() || '0'}
          onChange={(e) => {
            const cleanedValue = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(?!$)/, '');
            handleUpdate('totalQuantity', cleanedValue || '0');
          }}
          placeholder="0"
          disabled={disabled}
          className="h-9  font-medium text-center bg-slate-50 border border-slate-300 rounded-lg px-1 py-2"
        />
      </div>

      <div className="flex items-center justify-center">
        <Switch
          checked={variant.available}
          onCheckedChange={(checked) => handleUpdate('available', checked)}
          disabled={disabled}
          className="data-[state=checked]:bg-green-600 transform scale-75"
        />
      </div>
    </div>
  );
};

export default function ProductPricingEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [product, setProduct] = useState(location.state?.product || null);
  const [editedProduct, setEditedProduct] = useState(null);
  const [originalProduct, setOriginalProduct] = useState(null);
  const [localChanges, setLocalChanges] = useState(new Map());
  const [isLoading, setIsLoading] = useState(!product);
  const [isSaving, setIsSaving] = useState(false);
  const [bulkMrp, setBulkMrp] = useState('');
  const [bulkDiscount, setBulkDiscount] = useState('');
  const [showBulkPricing, setShowBulkPricing] = useState(false);
  const [showBulkDiscount, setShowBulkDiscount] = useState(false);
  const [productAlias, setProductAlias] = useState('');

  // Sale Window States
  const [saleStartDate, setSaleStartDate] = useState('');
  const [saleStartTime, setSaleStartTime] = useState('');
  const [saleEndDate, setSaleEndDate] = useState('');
  const [saleEndTime, setSaleEndTime] = useState('');
  const [initialSaleStartDate, setInitialSaleStartDate] = useState('');
  const [initialSaleStartTime, setInitialSaleStartTime] = useState('');
  const [initialSaleEndDate, setInitialSaleEndDate] = useState('');
  const [initialSaleEndTime, setInitialSaleEndTime] = useState('');

  // Gallery States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Check if vendor is event vendor
  const isEventVendor =
    user?.attributeValues?.find((attr) => attr.name === 'isEventVendor')?.value === 'true';

  useEffect(() => {
    if (product) {
      const productCopy = JSON.parse(JSON.stringify(product));
      setEditedProduct(productCopy);
      setOriginalProduct(JSON.parse(JSON.stringify(product)));
      setProductAlias(product.alias || '');

      // Parse Sale Window
      if (product.saleStartDate) {
        const [date, time] = product.saleStartDate.split('T');
        const d = date || '';
        const t = time ? time.substring(0, 5) : '';
        setSaleStartDate(d);
        setSaleStartTime(t);
        setInitialSaleStartDate(d);
        setInitialSaleStartTime(t);
      }
      if (product.saleEndDate) {
        const [date, time] = product.saleEndDate.split('T');
        const d = date || '';
        const t = time ? time.substring(0, 5) : '';
        setSaleEndDate(d);
        setSaleEndTime(t);
        setInitialSaleEndDate(d);
        setInitialSaleEndTime(t);
      }

      setIsLoading(false);
    } else {
      toast.error('Product details not found.');
      navigate('/product/pricing');
    }
  }, [product, id, navigate]);

  const handleVariantUpdate = (variantId, updates) => {
    if (!editedProduct || !originalProduct) return;

    const originalVariant = originalProduct.productVariants.find((v) => v.id === variantId);
    if (!originalVariant) return;

    if ('mrp' in updates && !('netPrice' in updates)) {
      const newMrp = updates.mrp;
      if (newMrp !== '' && !isNaN(Number.parseFloat(newMrp)) && Number.parseFloat(newMrp) >= 0) {
        updates.netPrice = newMrp;
      }
    }

    const isPriceBeingSet =
      ('mrp' in updates &&
        (originalVariant.mrp === 0 || originalVariant.mrp === null) &&
        updates.mrp !== '' &&
        Number.parseFloat(updates.mrp) >= 0) ||
      ('netPrice' in updates &&
        (originalVariant.netPrice === 0 || originalVariant.netPrice === null) &&
        updates.netPrice !== '' &&
        Number.parseFloat(updates.netPrice) >= 0);

    if (isPriceBeingSet && !originalVariant.available) {
      updates.available = true;
    }

    setEditedProduct((prev) => {
      const updatedProduct = {
        ...prev,
        productVariants: prev.productVariants.map((variant) =>
          variant.id === variantId ? { ...variant, ...updates } : variant
        ),
      };

      const updatedVariant = updatedProduct.productVariants.find((v) => v.id === variantId);

      const hasActualChanges =
        originalVariant.mrp?.toString() !== updatedVariant.mrp?.toString() ||
        originalVariant.netPrice?.toString() !== updatedVariant.netPrice?.toString() ||
        originalVariant.available !== updatedVariant.available ||
        originalVariant.totalQuantity?.toString() !== updatedVariant.totalQuantity?.toString();

      const isValidChange =
        !updatedVariant.available ||
        (updatedVariant.netPrice !== '' &&
          updatedVariant.netPrice !== null &&
          updatedVariant.netPrice !== undefined &&
          Number.parseFloat(updatedVariant.netPrice) >= 0);

      setLocalChanges((prevChanges) => {
        const newChanges = new Map(prevChanges);
        if (hasActualChanges && isValidChange) {
          newChanges.set(variantId, true);
        } else {
          newChanges.delete(variantId);
        }
        return newChanges;
      });

      return updatedProduct;
    });
  };

  const handleBulkAvailabilityToggle = (available) => {
    if (!editedProduct || !originalProduct) return;

    const updatedProduct = {
      ...editedProduct,
      productVariants: editedProduct.productVariants.map((variant) => ({
        ...variant,
        available,
      })),
    };

    setEditedProduct(updatedProduct);

    const newChanges = new Map();
    updatedProduct.productVariants.forEach((variant) => {
      const originalVariant = originalProduct.productVariants.find((v) => v.id === variant.id);
      const hasActualChanges =
        originalVariant &&
        (originalVariant.mrp?.toString() !== variant.mrp?.toString() ||
          originalVariant.netPrice?.toString() !== variant.netPrice?.toString() ||
          originalVariant.available !== variant.available);

      const isValidChange =
        !variant.available ||
        (variant.netPrice !== '' &&
          variant.netPrice !== null &&
          variant.netPrice !== undefined &&
          Number.parseFloat(variant.netPrice) >= 0);

      if (hasActualChanges && isValidChange) {
        newChanges.set(variant.id, true);
      }
    });
    setLocalChanges(newChanges);
  };

  const handleSave = async () => {
    if (!editedProduct || !user) return;
    setIsSaving(true);

    // Helper to combine date and time into ISO string
    const toISO = (date, time) => {
      if (!date) return undefined;
      const normalizedTime = time || '00:00';
      return `${date}T${normalizedTime}:00.000Z`;
    };

    const startDateTime = toISO(saleStartDate, saleStartTime);
    const endDateTime = toISO(saleEndDate, saleEndTime);

    const productToSave = {
      ...editedProduct,
      alias: productAlias || null,
      productVariants: editedProduct.productVariants.map((variant) => ({
        ...variant,
        mrp: Number.parseFloat(variant.mrp) || 0,
        netPrice: Number.parseFloat(variant.netPrice) || 0,
        totalQuantity: parseInt(variant.totalQuantity?.toString() || '0', 10),
      })),
    };

    const apiData = productToSave.productVariants.map((variant) => ({
      productId: productToSave.productId,
      variantId: variant?.variantId === null ? variant?.id : variant?.variantId,
      mrp: variant?.mrp,
      netPrice: variant?.netPrice,
      ...(variant?.unit && { unit: variant.unit }),
      available: variant?.available,
      requireParticipantInfo: variant?.requireParticipantInfo,
      ...(productToSave?.alias && { alias: productToSave.alias }),
      ...(startDateTime && { saleStartDate: startDateTime }),
      ...(endDateTime && { saleEndDate: endDateTime }),
      totalQuantity: variant?.totalQuantity,
    }));

    try {
      await axiosInstance.put(
        `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/products`,
        apiData,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            'X-Vendor-Id': user?.id,
            'X-USER-ROLE': USER_ROLES?.VENDOR,
          },
        }
      );
      toast.success('Product updated successfully!');
      navigate('/product/pricing');
    } catch (error) {
      console.error(error);
      if (
        error.message === 'ACCESS_RESTRICTED' ||
        error.response?.data?.toLowerCase().includes('view only') ||
        error.response?.data?.toLowerCase().includes('store front')
      ) {
        toast.error(
          'Access Restricted: Your account is in view-only mode. Contact your Administrator to enable editing.'
        );
      } else {
        toast.error('Failed to update product.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = useMemo(() => {
    return (
      localChanges.size > 0 ||
      (originalProduct?.alias || '') !== productAlias ||
      initialSaleStartDate !== saleStartDate ||
      initialSaleStartTime !== saleStartTime ||
      initialSaleEndDate !== saleEndDate ||
      initialSaleEndTime !== saleEndTime
    );
  }, [
    localChanges.size,
    originalProduct?.alias,
    productAlias,
    initialSaleStartDate,
    saleStartDate,
    initialSaleStartTime,
    saleStartTime,
    initialSaleEndDate,
    saleEndDate,
    initialSaleEndTime,
    saleEndTime,
  ]);

  const hasValidationErrors = useMemo(() => {
    return (
      editedProduct?.productVariants?.some((variant) => {
        if (!variant.available) return false;
        const mrp = Number.parseFloat(variant.mrp);
        const netPrice = Number.parseFloat(variant.netPrice);
        return (
          variant.mrp === '' ||
          variant.mrp === null ||
          isNaN(mrp) ||
          mrp < 0 ||
          variant.netPrice === '' ||
          variant.netPrice === null ||
          isNaN(netPrice) ||
          netPrice < 0 ||
          netPrice > mrp
        );
      }) || false
    );
  }, [editedProduct?.productVariants]);

  const displayImageUrl = editedProduct?.productImageUrls?.[0] || '/images/imagenotfound.webp';

  const productImages = useMemo(() => {
    return Array.isArray(editedProduct?.productImageUrls)
      ? editedProduct.productImageUrls
      : editedProduct?.productImageUrls
        ? [editedProduct.productImageUrls]
        : [];
  }, [editedProduct?.productImageUrls]);

  const handleImageClick = useCallback(() => {
    if (productImages.length > 0) {
      setSelectedImageIndex(0);
      setIsModalOpen(true);
    }
  }, [productImages.length]);

  const handlePrevImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : productImages.length - 1));
  }, [productImages.length]);

  const handleNextImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev < productImages.length - 1 ? prev + 1 : 0));
  }, [productImages.length]);

  if (isLoading || !editedProduct) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F77C06]"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/product/pricing')}
            className="h-9 w-9 rounded-full hover:bg-slate-100"
          >
            <ArrowLeft className="h-5 w-5 text-slate-700" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Edit Product</h1>
            <p className="text-xs text-slate-500">{editedProduct.productName}</p>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving || hasValidationErrors}
          className="bg-[#F77C06] hover:bg-orange-600 text-white font-bold h-9 px-6 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Main Content */}
      <div className="p-4 md:p-6 pb-20 md:pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Left Column: Product Info and Table */}
            <div className="flex-1 w-full space-y-6">
              {/* Product Info Section */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                <div className="flex flex-col md:flex-row items-start gap-4">
                  <button onClick={handleImageClick} className=" relative group cursor-pointer">
                    <img
                      src={displayImageUrl}
                      alt={editedProduct.productName}
                      className="w-44 md:w-24  lg:w-44 max-h-56 rounded-lg object-cover border-2 border-slate-100 group-hover:border-[#F77C06] transition-all"
                      onError={(e) => {
                        e.target.src = '/images/imagenotfound.webp';
                      }}
                    />
                    {productImages.length > 1 && (
                      <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1">
                        <ZoomIn className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </button>
                  <div className="flex-1">
                    <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-tight">
                      {editedProduct.productName}
                    </h2>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge
                        variant="secondary"
                        className="bg-slate-100 text-slate-700 hover:bg-slate-100"
                      >
                        <Package className="h-3 w-3 mr-1.5" />
                        {editedProduct.productVariants?.length} Variants
                      </Badge>
                    </div>

                    <div className="mt-4">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">
                        Product Alias (Optional)
                      </label>
                      <Input
                        type="text"
                        value={productAlias}
                        onChange={(e) => setProductAlias(e.target.value)}
                        placeholder="e.g., Organic Apple, Local Vendor Name"
                        className="h-10 text-sm font-medium border-slate-200 focus-visible:ring-[#F77C06]"
                        disabled={isSaving}
                      />
                      <p className="text-[11px] text-slate-400 mt-1 italic">
                        Custom name shown to your customers
                      </p>
                    </div>

                    {/* Show in Store Toggle */}
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-slate-900">Show in my store</p>
                        <p className="text-[10px] text-slate-500">
                          Enable or disable all variants at once
                        </p>
                      </div>
                      <Switch
                        checked={editedProduct.productVariants?.every((v) => v.available)}
                        onCheckedChange={handleBulkAvailabilityToggle}
                        disabled={isSaving}
                        className="data-[state=checked]:bg-green-600"
                      />
                    </div>

                    {/* Sale Window Section */}
                    {isEventVendor && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <h3 className="text-sm font-bold text-slate-900 mb-3">Sale Window</h3>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="text-[10px] font-bold text-slate-700 mb-1.5 uppercase block">
                              Start Date
                            </label>
                            <div className="relative">
                              <Input
                                type="date"
                                value={saleStartDate}
                                onChange={(e) => setSaleStartDate(e.target.value)}
                                className="h-10 text-sm pl-3 pr-3"
                                disabled={isSaving}
                              />
                              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-slate-700 mb-1.5 uppercase block">
                              Start Time
                            </label>
                            <div className="relative">
                              <Input
                                type="time"
                                value={saleStartTime}
                                onChange={(e) => setSaleStartTime(e.target.value)}
                                className="h-10 text-sm pl-3 pr-3"
                                disabled={isSaving}
                              />
                              <Clock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-[10px] font-bold text-slate-700 mb-1.5 uppercase block">
                              End Date
                            </label>
                            <div className="relative">
                              <Input
                                type="date"
                                value={saleEndDate}
                                onChange={(e) => setSaleEndDate(e.target.value)}
                                className="h-10 text-sm pl-3 pr-3"
                                disabled={isSaving}
                              />
                              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-slate-700 mb-1.5 uppercase block">
                              End Time
                            </label>
                            <div className="relative">
                              <Input
                                type="time"
                                value={saleEndTime}
                                onChange={(e) => setSaleEndTime(e.target.value)}
                                className="h-10 text-sm pl-3 pr-3"
                                disabled={isSaving}
                              />
                              <Clock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Variants Table Section */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <div className="min-w-[350px]">
                    <div className="grid grid-cols-[60px,64px,64px,64px,48px] sm:grid-cols-[1fr,86px,86px,66px,48px] gap-x-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                        Variant
                      </div>
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest text-center">
                        MRP
                      </div>
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest text-center">
                        Price
                      </div>
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest text-center">
                        Stock
                      </div>
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest text-center">
                        Live
                      </div>
                    </div>

                    <div className="divide-y divide-slate-100">
                      {editedProduct.productVariants?.map((variant) => (
                        <VariantTableRow
                          key={variant.id}
                          variant={variant}
                          onUpdate={handleVariantUpdate}
                          isChanged={localChanges.has(variant.id)}
                          disabled={isSaving}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {hasValidationErrors && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
                  <p className="text-sm text-red-700 font-medium">
                    Please ensure all available variants have a valid MRP and Selling Price. Selling
                    Price cannot be greater than MRP or negative.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Gallery Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl w-full p-0 bg-black">
          <div className="relative w-full h-[80vh]">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-50 p-4">
              <div className="flex justify-between items-center">
                <div className="bg-black/60 rounded-full px-4 py-2">
                  <p className="text-white font-semibold">
                    {selectedImageIndex + 1} / {productImages.length}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsModalOpen(false)}
                  className="bg-black/60 rounded-full p-2 hover:bg-black/80"
                >
                  <X className="h-6 w-6 text-white" />
                </Button>
              </div>
            </div>

            {/* Image Display */}
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={productImages[selectedImageIndex]}
                alt={`${editedProduct.productName} - Image ${selectedImageIndex + 1}`}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.src = '/images/imagenotfound.webp';
                }}
              />
            </div>

            {/* Navigation Buttons */}
            {productImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full p-3"
                >
                  <ChevronLeft className="h-6 w-6 text-white" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full p-3"
                >
                  <ChevronRight className="h-6 w-6 text-white" />
                </Button>
              </>
            )}

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <div className="bg-black/60 rounded-2xl p-3">
                <p className="text-white text-center font-semibold">{editedProduct.productName}</p>
                <p className="text-white/70 text-center text-sm mt-1">
                  Click arrows to navigate • Press ESC to close
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
